import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';
import { Grid } from '@mui/material';
import { v4 as generateUuid } from 'uuid';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import {
    UncontrolledGenericDrawer,
    DrawerBody,
    DrawerHeading,
    DrawerFooter,
    CreateTagButton,
    TagMultiselect,
    ValueModifier
} from '@/components/common';
import { TextField } from '@/components/rhf-mui';
import { useDrawers, useToastNotification, useUrlParams } from '@/hooks';
import { DiscountActive, SaveIcon } from '@/resources/icons';
import { toIntegerCurrency, toWholeCurrency } from '@/util';
import {
    createDiscount,
    updateDiscount,
    getPaginatedDiscounts,
    getPaginatedTags
} from '@/modules/dataWrangler/dataWrangler';
import { closeDrawer } from '@/state/actions';
import {
    DISCOUNT_BASE_PARAM,
    TAG_BASE_PARAM,
    VALUE_PERCENTAGE_TYPE
} from '@/constants';
import makeStyles from './styles';

const DiscountDrawer = () => {
    const classes = makeStyles();
    const location = useLocation();

    const { getUrlParams } = useUrlParams();
    const currentDiscountUrlParams = getUrlParams({
        base: DISCOUNT_BASE_PARAM
    });
    const currentTagUrlParams = getUrlParams({ base: TAG_BASE_PARAM });

    const { getDrawer } = useDrawers();
    const dispatch = useDispatch();
    const { triggerToast } = useToastNotification();
    const [saving, setSaving] = useState(false);

    const methods = useForm({
        nativeValidation: false,
        criteriaMode: 'all'
    });

    const {
        handleSubmit,
        register,
        setValue,
        watch,
        clearErrors,
        formState: { errors }
    } = methods;

    const allDiscounts =
        useSelector((state) => state.user?.activeBusiness?.discounts) || [];
    const discountDrawer = getDrawer('discount');

    const discount = discountDrawer?.discount;
    const edit = discountDrawer?.edit;
    const updatedTags = discountDrawer?.updatedTags;
    const currentValues = discountDrawer?.currentValues;
    const drawerOpen = Boolean(discountDrawer);

    useEffect(() => {
        // Set value handled by value modifier.
        register('value', {
            required: 'Please enter a valid number',
            validate: {
                number: (value) =>
                    !Number.isNaN(value) || 'Please enter a valid number'
            }
        });
        register('type');

        // To set the default values.
        setValue(
            'value',
            currentValues?.value ||
                (discount?.value && toWholeCurrency(discount?.value)) ||
                ''
        );
        setValue(
            'type',
            currentValues?.type || discount?.type || VALUE_PERCENTAGE_TYPE
        );
        setValue('name', currentValues?.name || discount?.name || '');
        setValue('tags', updatedTags || discount?.tags || []);
    }, [register, setValue, currentValues, updatedTags, discount, drawerOpen]);

    const handleCloseDrawer = () => {
        dispatch(closeDrawer('discount'));
        clearErrors();
    };

    const checkIfUnique = (newName) => {
        const exists = allDiscounts.find(
            (previous) =>
                previous.name === newName &&
                previous.discountId !== discount?.discountId
        );

        return !exists;
    };

    const onSubmit = async (data) => {
        setSaving(true);

        // Remove "tags" key to replace with "tagIds" below
        const { tags, ...discountData } = data;

        // Strip out only the tagIds to add to discount object
        const tagIds = tags?.map((tagData) => tagData?.tagId);

        if (edit) {
            await updateDiscount({
                ...discountData,
                value: toIntegerCurrency(parseFloat(data.value)),
                discountId: discount.discountId,
                tagIds
            });
        } else {
            await createDiscount({
                ...discountData,
                value: toIntegerCurrency(parseFloat(data.value)),
                discountId: generateUuid(),
                tagIds
            });
        }

        if (location.pathname === '/items/discounts') {
            getPaginatedDiscounts(currentDiscountUrlParams);
        }

        if (location.pathname === '/items/tags') {
            getPaginatedTags(currentTagUrlParams);
        }

        setSaving(false);
        handleCloseDrawer();

        triggerToast({
            message: edit
                ? `Discount "${data.name}" updated`
                : `Discount "${data.name}" saved`,
            action: 'saved',
            icon: <SaveIcon />
        });
    };

    return (
        <UncontrolledGenericDrawer
            open={drawerOpen}
            close={handleCloseDrawer}
            id="discount"
        >
            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={classes.form}
                >
                    <DrawerHeading
                        startAdornment={<DiscountActive />}
                        title={edit ? 'Edit Discount' : 'Add Discount'}
                    />
                    <DrawerBody className={classes.itemList}>
                        <Grid item>
                            <TextField
                                fullWidth
                                name="name"
                                id="discount-drawer-name"
                                label="Discount Name"
                                rules={{
                                    required: 'A discount name is required',
                                    validate: {
                                        unique: (value) =>
                                            checkIfUnique(value) ||
                                            'Discount names must be unique'
                                    }
                                }}
                                inputProps={{ maxLength: 256 }}
                            />
                        </Grid>
                        <Grid item>
                            <ValueModifier
                                error={Boolean(errors?.value?.message)}
                                helperText={errors?.value?.message}
                                title="Discount Rate"
                                id="discount"
                                type={watch('type', VALUE_PERCENTAGE_TYPE)}
                                value={watch('value')}
                                onUpdate={({ value, type }) => {
                                    setValue('value', value, {
                                        shouldValidate: true
                                    });
                                    setValue('type', type);
                                }}
                            />
                        </Grid>
                        <Grid item>
                            <TagMultiselect />
                        </Grid>
                        <Grid item container justifyContent="flex-end">
                            <CreateTagButton
                                type="discount"
                                item={discount}
                                currentValues={watch()}
                                edit={edit}
                            />
                        </Grid>
                    </DrawerBody>

                    <DrawerFooter
                        submitButtonId="Adddiscount"
                        acceptOptions={{
                            label: 'Save',
                            type: 'submit'
                        }}
                        cancelOptions={{
                            label: 'Cancel',
                            action: handleCloseDrawer
                        }}
                        saving={saving}
                    />
                </form>
            </FormProvider>
        </UncontrolledGenericDrawer>
    );
};

export default DiscountDrawer;
